import * as sdk from '../sdk';
import { displayFailMessage } from './notificationActions';
import { toaster } from '../common';
import {
  pendingUpload,
  endPendingUpload,
  refreshProfile,
  refreshAccount,
  storeNewPrivacy,
  setGalleryLoading,
  killGalleryLoading,
  UploadMediaFormDataType,
  getPhotoUploadResponse,
} from './profileActions';
import store, { AppDispatch, RootState } from '../store';
import * as Sentry from '@sentry/react';
import {
  hideUploadSnackbar,
  incrementUploadSnackbar,
  updateUploadStatusSnackbar,
  updateUploadUploadTypeSnackbar,
} from './uploadSnackbarActions';
import { IUploadSnackbarItem, UploadSnackbarType } from '../reducers/types/uploadSnackbar';
import { MAX_RETRY } from '../reducers/uploadSnackbar';
import MasterConfig from '../config/Master';
import { uploadPhotoToS3 } from '../utils/photoUpload';

export const galleryBulkUploadMedia = (mediaType: UploadSnackbarType) => {
  return async (dispatch: AppDispatch, getState: RootState): Promise<void> => {
    const { uploadSnackbar } = getState();

    if (typeof uploadSnackbar === 'undefined') {
      return;
    }

    dispatch(updateUploadUploadTypeSnackbar(mediaType));
    const freshItems = uploadSnackbar.items.filter((item: IUploadSnackbarItem) => item.status === '');

    const uploads = {
      photo: async () => {
        for (const item of freshItems) {
          await galleryUploadPhoto(
            {
              id: item.id,
              file: item.file.edited,
              isPrivate: item.file.private,
              isCropped: true,
            },
            dispatch,
            getState
          );
        }
      },
      video: async () => {
        for (const item of freshItems) {
          await dispatch(
            galleryUploadVideo({
              id: item.id,
              file: item.file.raw,
              isPrivate: item.file.private,
            })
          );
        }
      },
    };

    if (typeof uploads[mediaType] !== 'undefined') {
      uploads[mediaType]();
    }
  };
};

export const retryProcessPhoto = async ({
  path,
  isPrivate,
  isCropped,
  dispatch,
  snackbarItemId,
}: {
  path: string;
  isPrivate: boolean;
  isCropped: boolean;
  dispatch: AppDispatch;
  snackbarItemId: string;
}): Promise<void> => {
  const { uploadSnackbar } = store.getState();
  const uploadSnackbarItem = uploadSnackbar.items.find((item: IUploadSnackbarItem) => item.id === snackbarItemId);

  if (uploadSnackbarItem && uploadSnackbarItem.retry >= MAX_RETRY) {
    dispatch(updateUploadStatusSnackbar({ id: snackbarItemId, status: 'failed' }));
    return;
  }

  dispatch(updateUploadStatusSnackbar({ id: snackbarItemId, status: 'retry' }));
  dispatch(incrementUploadSnackbar({ id: snackbarItemId }));

  processPhoto({
    path,
    isPrivate,
    isCropped,
    dispatch,
    snackbarItemId,
  });
};

export const processPhoto = async ({
  path,
  isPrivate,
  isCropped,
  dispatch,
  snackbarItemId,
}: {
  path: string;
  isPrivate: boolean;
  isCropped: boolean;
  dispatch: AppDispatch;
  snackbarItemId: string;
}): Promise<void> => {
  try {
    await sdk.processPhoto({
      path: path,
      private: isPrivate,
      is_cropped: isCropped,
    });

    dispatch(updateUploadStatusSnackbar({ id: snackbarItemId, status: 'success' }));

    dispatch(refreshProfile());
    dispatch(refreshAccount());
  } catch (error) {
    Sentry.captureException(error, { tags: { component: 'Photos', action: 'Upload' } });
    dispatch(updateUploadStatusSnackbar({ id: snackbarItemId, status: 'failed' }));
  }
};

export const refreshUserPhoto = async ({
  path,
  isPrivate,
  isCropped,
  dispatch,
  snackbarItemId,
}: {
  path: string;
  isPrivate: boolean;
  isCropped: boolean;
  dispatch: AppDispatch;
  snackbarItemId: string;
}): Promise<void> => {
  try {
    return await processPhoto({
      path,
      isPrivate,
      isCropped,
      dispatch,
      snackbarItemId,
    });
  } catch (error) {
    dispatch(updateUploadStatusSnackbar({ id: snackbarItemId, status: 'failed' }));

    Sentry.captureException('Bulk upload photo failed to upload/processed', {
      extra: {
        photoPath: path,
        data: error,
      },
    });
  }
};

export const galleryUploadPhoto = async (
  formData: UploadMediaFormDataType,
  dispatch: any,
  getState: any
): Promise<any> => {
  const { isPrivate, file, isCropped, id, fileExtension } = formData;

  try {
    const useDirectUpload = getState().profile.direct_s3_upload;

    if (!useDirectUpload) {
      dispatch(hideUploadSnackbar());
      dispatch(updateUploadStatusSnackbar({ id, status: 'failed' }));

      throw new Error('Error not isDirectUpload');
    }

    const photoUploadResponse = await getPhotoUploadResponse(useDirectUpload, fileExtension, isPrivate, file);

    const photoResponse = photoUploadResponse.data;

    dispatch(updateUploadStatusSnackbar({ id, status: 'uploading' }));

    try {
      await uploadPhotoToS3(
        {
          isCropped: isCropped,
          photoFile: file,
        },
        photoResponse
      );

      return await new Promise(
        resolve =>
          setTimeout(async () => {
            const response = await refreshUserPhoto({
              path: photoResponse.data.path,
              isPrivate,
              isCropped,
              dispatch,
              snackbarItemId: id,
            });

            resolve(response);
          }, 6000) // lets wait for the lambda to fully process the image resizing
      );
    } catch (error) {
      Sentry.captureException('Bulk upload photo failed to upload to s3. error: ', {
        extra: {
          photoPath: photoResponse.data.path,
          data: error,
        },
      });

      dispatch(updateUploadStatusSnackbar({ id, status: 'failed' }));
    }
  } catch (error) {
    console.warn(error);
    // might need to update all snackbar items to failed
    dispatch(updateUploadStatusSnackbar({ id, status: 'failed' }));
  }
};

export const galleryUploadVideo = (formData: Omit<UploadMediaFormDataType, 'isCropped'>): unknown => {
  return async (dispatch: AppDispatch, getState: RootState) => {
    const { isPrivate, file, id } = formData;
    const mediaType = 'video/mp4';

    dispatch(updateUploadStatusSnackbar({ id, status: 'uploading' }));

    return await sdk
      .addNewMedia(getState().auth.jwToken, {
        mimetype: mediaType,
        private: isPrivate,
      })
      .then(response => {
        const apiResponse = response.data.data;
        const { uploadUrl } = response.data.meta;

        const newFile = getUploadFile(apiResponse, file, mediaType);

        const setTimeoutId = setTimeout(async () => {
          await sdk.uploadToS3(uploadUrl, newFile).then(
            () => {
              dispatch(updateUploadStatusSnackbar({ id, status: 'success' }));
              dispatch(refreshProfile());
              dispatch(refreshAccount()); // lets wait for the lambda to fully process the image resizing
              clearTimeout(setTimeoutId);
            },
            () => {
              clearTimeout(setTimeoutId);
              dispatch(markVideoUploadFailed(apiResponse.id));
              dispatch(updateUploadStatusSnackbar({ id, status: 'failed' }));
            }
          );
        }, 5000);
      })
      .catch(() => {
        dispatch(updateUploadStatusSnackbar({ id, status: 'failed' }));
      });
  };
};

export const addNewMedia = (formData: any, isPrivate: any, mediaType: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    dispatch(pendingUpload(isPrivate));

    const mimeType = mediaType ? mediaType : formData.type;

    return sdk
      .addNewMedia(getState().auth.jwToken, {
        mimetype: mimeType,
        private: isPrivate,
      })
      .then(response => {
        const apiResponse = response.data.data;
        const { uploadUrl } = response.data.meta;

        const newFile = getUploadFile(apiResponse, formData, mediaType);

        sdk.uploadToS3(uploadUrl, newFile).then(
          () => {
            dispatch(endPendingUpload(isPrivate));
            dispatch(refreshProfile());
            dispatch(refreshAccount()); // lets wait for the lambda to fully process the image resizing
          },
          () => {
            // dispatch(deleteMedia(apiResponse.id));
            dispatch(endPendingUpload(isPrivate));
          }
        );
      })
      .catch(error => {
        uploadErrorFlow(error, isPrivate, dispatch);
      });
  };
};

const getUploadFile = (apiResponse: any, formData: any, mediaType: any): any => {
  const lastModifiedDate = new Date();
  const fileExtension = mediaType
    ? mediaType.substr(mediaType.lastIndexOf('/') + 1)
    : formData.type.substr(formData.type.lastIndexOf('/') + 1);
  const fileName = apiResponse.path + '.' + fileExtension;
  const mimeType = mediaType ? mediaType : formData.type;

  if (formData instanceof Blob) {
    return formData;
  }

  try {
    return new File([formData], fileName, { type: mimeType });
  } catch (err) {
    console.warn(err);
    const newFile = new Blob([formData], { type: mimeType });

    (newFile as any)['lastModifiedDate'] = lastModifiedDate;
    (newFile as any)['lastModified'] = +lastModifiedDate;
    (newFile as any)['name'] = fileName;

    return newFile;
  }
};

export const deleteMedia = (mediaId: any): any => {
  return (_dispatch: AppDispatch, getState: RootState) => {
    return sdk
      .deleteMedia(getState().auth.jwToken, mediaId)
      .then(() => {
        /*dispatch(refreshProfile()); /* refresh main photo */
      })
      .catch(error => {
        console.error(error);
      });
  };
};

export const setNewMediaPrivacy = (mediaId: any, mediaKey: any): any => {
  // set new photo privacy.
  return (dispatch: AppDispatch, getState: RootState) => {
    // notify store
    dispatch(storeNewPrivacy(mediaKey));
    // notify api
    return sdk.updateMediaPrivacy(getState().auth.jwToken, mediaId);
  };
};

export const markVideoUploadFailed = (mediaId: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    return sdk.markUploadFailed(getState().auth.jwToken, mediaId);
  };
};

const uploadErrorFlow = (error: any, isPrivate: any, dispatch: any): any => {
  // toast error
  dispatch(endPendingUpload(isPrivate));
  let msg = null;

  if (error.response && error.response.data) {
    if (error.response.data.video) {
      msg = error.response.data.video[0];
    } else if (error.response.data.errors) {
      msg = error.response.data.errors[0].detail;
    }
  }

  if (msg) {
    toaster.warn(msg);
  } else {
    dispatch(
      displayFailMessage({
        info: 'Error uploading video. Please try again or ',
        linkInfo: 'contact support.',
        url: `${MasterConfig.FULL_URL}/faq/giving-feedback`,
        newWindow: true,
      })
    );
  }
};

export const bulkDelete = (mediaIds: any): any => {
  const formData = {
    video: [...mediaIds['video']['public'], ...mediaIds['video']['private']],
    photo: [...mediaIds['photo']['public'], ...mediaIds['photo']['private']],
  };

  return (dispatch: AppDispatch) => {
    dispatch(setGalleryLoading());
    return sdk
      .bulkDelete(formData)
      .then(response => {
        dispatch(refreshProfile());
        dispatch(refreshAccount());
        dispatch(killGalleryLoading());
        toaster.success('Successfully deleted photos/videos.');

        return response.data.data;
      })
      .catch(error => {
        dispatch(killGalleryLoading());
        dispatch(
          displayFailMessage({
            info: 'Error deleting photos/videos. Please try again or',
            linkInfo: 'contact support.',
            url: `${MasterConfig.FULL_URL}/faq/giving-feedback`,
            newWindow: true,
          })
        );

        console.error(error);
      });
  };
};

export const bulkTogglePrivacy = (mediaIds: any, privacy: any): any => {
  const formData = {
    video: [...mediaIds['video']['public'], ...mediaIds['video']['private']],
    photo: [...mediaIds['photo']['public'], ...mediaIds['photo']['private']],
    privacy: privacy,
  };

  return (dispatch: AppDispatch) => {
    dispatch(setGalleryLoading());
    return sdk
      .bulkTogglePrivacy(formData)
      .then(response => {
        dispatch(refreshProfile());
        dispatch(refreshAccount());
        dispatch(killGalleryLoading());

        toaster.success(`Successfully updated the privacy of photos/videos to ${privacy}.`);
        return response.data.data;
      })
      .catch(error => {
        dispatch(killGalleryLoading());
        dispatch(
          displayFailMessage({
            info: 'Error updating privacy of photos/videos. Please try again or',
            linkInfo: 'contact support.',
            url: `${MasterConfig.FULL_URL}/faq/giving-feedback`,
            newWindow: true,
          })
        );

        console.error(error);
      });
  };
};
